import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Navbar } from '@packages/ui/layout';
import type { NavMenuGroup } from '@packages/ui/layout';
import CheckPermissions from '@/utils/permission/checkPermission';

interface MobileUserSectionProps {
  sectionLabel?: string;
  children?: React.ReactNode;
}

interface MobileMenuRendererProps {
  groups: NavMenuGroup[];
  userSection?: MobileUserSectionProps & { children: React.ReactNode };
}

/**
 * Generic mobile menu renderer.
 * Renders groups as MobileNavItem (flat) or MobileAccordion (with children),
 * wrapping in CheckPermissions when a group declares a permission.
 */
const MobileMenuRenderer: React.FC<MobileMenuRendererProps> = ({ groups, userSection }) => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <Navbar.MobileBackdrop semanticId="mobileBackdrop" />
      <Navbar.MobileMenu semanticId="mobileMenu">
        <Navbar.MobileMenuSection label="Menu" semanticId="mobileMenuSection">
          {groups.filter((g) => g.visible).map((group) => {
            const hasChildren = group.children && group.children.length > 0;

            const content = hasChildren ? (
              <Navbar.MobileAccordion
                key={`mobile-${group.id}`}
                label={group.label}
                semanticId={`mobile${group.id}Accordion`}
              >
                {group.children!.filter((c) => c.visible).map((child) => (
                  <Navbar.MobileAccordionItem
                    key={child.id}
                    active={isActive(child.path)}
                    semanticId={`mobile${child.id}`}
                  >
                    <NavLink to={child.path}>{child.label}</NavLink>
                  </Navbar.MobileAccordionItem>
                ))}
              </Navbar.MobileAccordion>
            ) : (
              <Navbar.MobileNavItem
                key={`mobile-${group.id}`}
                active={isActive(group.path ?? '')}
                semanticId={`mobile${group.id}`}
              >
                <NavLink to={group.path!}>{group.label}</NavLink>
              </Navbar.MobileNavItem>
            );

            if (group.permission) {
              return (
                <CheckPermissions
                  key={`mobile-${group.id}`}
                  type={group.permission.type}
                  urlName={group.permission.urlName}
                >
                  {content}
                </CheckPermissions>
              );
            }

            return content;
          })}
        </Navbar.MobileMenuSection>

        {userSection && (
          <Navbar.MobileMenuSection
            label={userSection.sectionLabel}
            semanticId="mobileUserSection"
          >
            {userSection.children}
          </Navbar.MobileMenuSection>
        )}
      </Navbar.MobileMenu>
    </>
  );
};

export { MobileMenuRenderer };
